(function () {
  const rootSelector = "[data-azzurro-world-optics]";
  const cardSelector = "[data-azwo-lens-card]";
  const panelSelector = "[data-azzurro-world-optics-panel]";
  const envButtonSelector = "[data-azwo-environment]";
  const envPanelSelector = "[data-azwo-environment-panel]";
  const sliderSelector = "[data-azwo-slider]";
  const initializedRoots = new WeakSet();

  function normalizeLensKey(root, lensKey) {
    const cards = Array.from(root.querySelectorAll(cardSelector));
    const match = cards.find((card) => card.dataset.lensKey === lensKey);
    return (match || cards[0])?.dataset.lensKey || "";
  }

  function getLensIndex(root, lensKey) {
    const activeLensKey = normalizeLensKey(root, lensKey);
    const cards = Array.from(root.querySelectorAll(cardSelector));
    const index = cards.findIndex((card) => card.dataset.lensKey === activeLensKey);
    return index >= 0 ? index : 0;
  }

  function clampPercent(percent) {
    return Math.max(5, Math.min(95, percent));
  }

  function getSliderPercent(slider) {
    const handle = slider.querySelector("[data-azwo-handle]");
    const resize = slider.querySelector("[data-azwo-resize]");
    const handlePercent = parseFloat(handle?.getAttribute("aria-valuenow") || handle?.style.left);
    const resizePercent = parseFloat(resize?.style.width);

    if (!Number.isNaN(handlePercent)) return handlePercent;
    if (!Number.isNaN(resizePercent)) return resizePercent;
    return 50;
  }

  function getSliderWidth(slider, root) {
    const sliderWidth = slider.getBoundingClientRect().width || slider.clientWidth;
    if (sliderWidth > 0) return sliderWidth;

    const bottomEl = root?.querySelector("[data-azwo-bottom]");
    const bottomWidth = bottomEl?.getBoundingClientRect().width || bottomEl?.clientWidth;
    return bottomWidth > 0 ? bottomWidth : 0;
  }

  function setSliderPercent(slider, percent, width) {
    const safePercent = clampPercent(percent);
    const resize = slider.querySelector("[data-azwo-resize]");
    const handle = slider.querySelector("[data-azwo-handle]");
    const sliderWidth = width || slider.clientWidth;

    if (resize) {
      resize.style.width = `${safePercent}%`;
      if (sliderWidth > 0) {
        resize.style.setProperty("--azwo-slider-width", `${sliderWidth}px`);
      }
    }

    if (handle) {
      handle.style.left = `${safePercent}%`;
      handle.setAttribute("aria-valuenow", String(Math.round(safePercent)));
    }
  }

  function isActiveSlider(root, slider) {
    const panel = slider.closest(panelSelector);
    const environmentPanel = slider.closest(envPanelSelector);
    const activeLens = root.dataset.activeLens;
    const activeEnvironment = root.dataset.activeEnvironment || "ocean";

    return (
      panel?.dataset.lensKey === activeLens &&
      environmentPanel?.dataset.environment === activeEnvironment &&
      !environmentPanel.hidden
    );
  }

  function syncSliderWidth(root, slider) {
    const resize = slider.querySelector("[data-azwo-resize]");
    if (!resize) return;

    const sliderWidth = getSliderWidth(slider, root);
    if (sliderWidth <= 0) return;

    resize.querySelectorAll("img").forEach((image) => {
      image.style.width = `${sliderWidth}px`;
      image.style.height = "auto";
    });

    setSliderPercent(slider, getSliderPercent(slider), sliderWidth);
  }

  function syncSliderWidths(root) {
    root.querySelectorAll(sliderSelector).forEach((slider) => {
      if (isActiveSlider(root, slider)) {
        syncSliderWidth(root, slider);
      }
    });
  }

  function queueSliderSync(root) {
    if (!root) return;

    syncSliderWidths(root);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncSliderWidths(root);
      });
    });

    window.setTimeout(() => {
      syncSliderWidths(root);
    }, 100);
  }

  function setEnvironment(root, environment) {
    const activeEnvironment = environment || root.dataset.activeEnvironment || "ocean";
    root.dataset.activeEnvironment = activeEnvironment;

    root.querySelectorAll(envButtonSelector).forEach((button) => {
      const isActive = button.dataset.azwoEnvironment === activeEnvironment;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-selected", isActive ? "true" : "false");
    });

    root.querySelectorAll(envPanelSelector).forEach((panel) => {
      const isActive = panel.dataset.environment === activeEnvironment;
      panel.classList.toggle("hidden", !isActive);
      panel.toggleAttribute("hidden", !isActive);
    });

    queueSliderSync(root);
  }

  function isDesktopGridLayout() {
    return window.matchMedia("(min-width: 960px)").matches;
  }

  function applyManualSlides(root, activeIndex, target = "both") {
    const translate = `translate3d(${-activeIndex * 100}%, 0, 0)`;

    if (target === "top" || target === "both") {
      const topWrapper = root.querySelector("[data-azwo-top] .swiper-wrapper");
      if (topWrapper) {
        topWrapper.style.transform = translate;
      }
    }

    if (target === "bottom" || target === "both") {
      const bottomWrapper = root.querySelector("[data-azwo-bottom] .swiper-wrapper");
      if (bottomWrapper) {
        bottomWrapper.style.transform = translate;
      }
    }
  }

  function setLens(root, lensKey, options = {}) {
    if (!root) return;

    const activeLensKey = normalizeLensKey(root, lensKey);
    if (!activeLensKey) return;

    const activeIndex = getLensIndex(root, activeLensKey);
    root.dataset.activeLens = activeLensKey;

    root.querySelectorAll(cardSelector).forEach((card) => {
      const isActive = card.dataset.lensKey === activeLensKey;
      card.classList.toggle("is-active", isActive);
      card.setAttribute("aria-pressed", isActive ? "true" : "false");
    });

    root.querySelectorAll(panelSelector).forEach((panel) => {
      panel.classList.toggle("is-active", panel.dataset.lensKey === activeLensKey);
    });

    const state = root.azwoState || {};
    if (state.topSwiper) {
      if (options.fromSwiper !== true) {
        state.topSwiper.slideTo(activeIndex, 0);
      }
    } else if (!state.isDesktopGrid) {
      applyManualSlides(root, activeIndex, "top");
    }

    if (state.bottomSwiper) {
      state.bottomSwiper.slideTo(activeIndex, 0);
    } else {
      applyManualSlides(root, activeIndex, "bottom");
    }

    root.azwoState = {
      ...state,
      activeIndex,
    };

    setEnvironment(root, root.dataset.activeEnvironment || "ocean");
  }

  function handlePointerDrag(event) {
    const handle = event.target.closest("[data-azwo-handle]");
    if (!handle) return;

    const slider = handle.closest(sliderSelector);
    if (!slider) return;

    event.preventDefault();

    const move = (moveEvent) => {
      const point = moveEvent.touches ? moveEvent.touches[0] : moveEvent;
      const rect = slider.getBoundingClientRect();
      const percent = ((point.clientX - rect.left) / rect.width) * 100;
      setSliderPercent(slider, percent);
    };

    const stop = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", stop);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", stop);
    };

    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", stop);
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchend", stop);
  }

  function createTopMobileSwiper(root, topEl, activeIndex) {
    if (!window.Swiper || !topEl) return null;

    const topSwiper = new Swiper(topEl, {
      autoHeight: true,
      loop: false,
      rewind: true,
      spaceBetween: 0,
      slidesPerView: 1,
      slideToClickedSlide: true,
      watchSlidesProgress: true,
      threshold: 30,
      navigation: {
        nextEl: root.querySelector("[data-azwo-next]"),
        prevEl: root.querySelector("[data-azwo-prev]"),
      },
    });

    topSwiper.on("slideChange", () => {
      const activeCard = topSwiper.slides[topSwiper.activeIndex];
      if (activeCard?.dataset.lensKey) {
        setLens(root, activeCard.dataset.lensKey, { fromSwiper: true });
      }
    });

    topSwiper.slideTo(activeIndex, 0);
    return topSwiper;
  }

  function initSwipers(root) {
    const topEl = root.querySelector("[data-azwo-top]");
    const bottomEl = root.querySelector("[data-azwo-bottom]");
    if (!topEl || !bottomEl) return false;

    const activeIndex = getLensIndex(root, root.dataset.activeLens);
    const desktopGrid = isDesktopGridLayout();
    let topSwiper = null;
    let bottomSwiper = null;

    if (window.Swiper) {
      if (!desktopGrid) {
        topSwiper = createTopMobileSwiper(root, topEl, activeIndex);
      }

      bottomSwiper = new Swiper(bottomEl, {
        allowTouchMove: false,
        autoHeight: true,
        loop: false,
        rewind: true,
        spaceBetween: 0,
        slidesPerView: 1,
        speed: 0,
        pagination: {
          el: bottomEl.querySelector(".swiper-pagination"),
          clickable: true,
        },
      });
    }

    root.azwoState = {
      topSwiper,
      bottomSwiper,
      activeIndex,
      isDesktopGrid: desktopGrid,
    };

    if (!topSwiper && !desktopGrid) {
      applyManualSlides(root, activeIndex, "top");
    }

    if (bottomSwiper) {
      bottomSwiper.slideTo(activeIndex, 0);
    } else {
      applyManualSlides(root, activeIndex, "bottom");
    }

    return true;
  }

  function syncLayoutMode(root) {
    const state = root.azwoState || {};
    const wasDesktop = state.isDesktopGrid === true;
    const nowDesktop = isDesktopGridLayout();
    if (wasDesktop === nowDesktop) return;

    if (nowDesktop) {
      if (state.topSwiper) {
        state.topSwiper.destroy(true, true);
      }
      const topWrapper = root.querySelector("[data-azwo-top] .swiper-wrapper");
      if (topWrapper) topWrapper.style.transform = "";
      root.azwoState = {
        ...state,
        topSwiper: null,
        isDesktopGrid: true,
      };
    } else {
      const topEl = root.querySelector("[data-azwo-top]");
      const activeIndex = getLensIndex(root, root.dataset.activeLens);
      const topSwiper = createTopMobileSwiper(root, topEl, activeIndex);
      root.azwoState = {
        ...state,
        topSwiper,
        isDesktopGrid: false,
      };
    }
  }

  function mapLensValueToKey(value) {
    const v = (value || "").toLowerCase();
    if (v.includes("rose") && v.includes("copper mirror")) return "rose-copper";
    if (v.includes("blue mirror")) return "grey-blue";
    if (v.includes("yellow") && v.includes("green mirror")) return "yellow-green";
    if (v.includes("brown") && v.includes("green mirror")) return "yellow-green";
    return "grey-green";
  }

  function initRoot(root) {
    if (!root || initializedRoots.has(root)) return;
    if (!initSwipers(root)) return;

    initializedRoots.add(root);
    root.dataset.activeEnvironment = root.getAttribute("data-active-environment") || "ocean";

    root.addEventListener("click", (event) => {
      const lensCard = event.target.closest(cardSelector);
      if (lensCard && root.contains(lensCard)) {
        setLens(root, lensCard.dataset.lensKey);
        return;
      }

      const nextButton = event.target.closest("[data-azwo-next]");
      const prevButton = event.target.closest("[data-azwo-prev]");
      if ((nextButton || prevButton) && root.contains(nextButton || prevButton)) {
        if (root.azwoState?.topSwiper) return;

        const cards = Array.from(root.querySelectorAll(cardSelector));
        const currentIndex = root.azwoState?.activeIndex || getLensIndex(root, root.dataset.activeLens);
        const direction = nextButton ? 1 : -1;
        const nextIndex = (currentIndex + direction + cards.length) % cards.length;
        const nextCard = cards[nextIndex];

        if (nextCard) {
          setLens(root, nextCard.dataset.lensKey);
        }
        return;
      }

      const envButton = event.target.closest(envButtonSelector);
      if (envButton && root.contains(envButton)) {
        setEnvironment(root, envButton.dataset.azwoEnvironment);
      }
    });

    root.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;

      const lensCard = event.target.closest(cardSelector);
      if (lensCard && root.contains(lensCard)) {
        event.preventDefault();
        setLens(root, lensCard.dataset.lensKey);
      }
    });

    root.addEventListener("mousedown", handlePointerDrag);
    root.addEventListener("touchstart", handlePointerDrag, { passive: false });

    root.querySelectorAll(sliderSelector).forEach((slider) => {
      setSliderPercent(slider, 50);
    });

    root.querySelectorAll(`${sliderSelector} img`).forEach((image) => {
      image.addEventListener("load", () => {
        queueSliderSync(root);
      });
    });

    setLens(root, root.dataset.activeLens, { fromSwiper: true });
  }

  function initAll() {
    document.querySelectorAll(rootSelector).forEach(initRoot);
  }

  window.AzzurroWorldOptics = {
    init: initAll,
    setLens(root, lensKey, options) {
      setLens(root, lensKey, options);
    },
    setEnvironment(root, environment) {
      setEnvironment(root, environment);
    },
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("change", (event) => {
    const target = event.target;
    if (!target || target.tagName !== "INPUT") return;
    if (target.name !== "Lens Color" && target.name !== "Lens Options") return;
    const lensKey = mapLensValueToKey(target.value);
    document.querySelectorAll(rootSelector).forEach((root) => {
      setLens(root, lensKey);
    });
  });

  window.addEventListener("resize", () => {
    document.querySelectorAll(rootSelector).forEach((root) => {
      syncLayoutMode(root);
      queueSliderSync(root);
    });
  });
})();
